// Grouping model for the Issues List's "Group" control (V5 toolbar layout).
// Pure functions over the already-filtered, already-risk-sorted row list so the
// list body can render collapsible sections without re-deriving anything.

import type { IssueSeverity } from '@/shared/constants/issue-status'

import { prettyName } from './mandal-name'

import type { GisIssue } from '../api/types'

export type IssueGroupBy =
  | 'none'
  | 'site'
  | 'question'
  | 'mandal'
  | 'department'
  | 'entity'
  | 'category'
  | 'severity'
  | 'status'

export const GROUP_OPTIONS: { value: IssueGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'site', label: 'Site' }, // the default grouping — issues live at sites
  { value: 'question', label: 'Question' }, // survey teams work question-first
  { value: 'mandal', label: 'Mandal' },
  { value: 'department', label: 'Department' },
  { value: 'entity', label: 'Entity' },
  { value: 'category', label: 'Issue type' },
  { value: 'severity', label: 'Importance' },
  { value: 'status', label: 'Status' },
]

export const groupLabelFor = (g: IssueGroupBy): string =>
  GROUP_OPTIONS.find((o) => o.value === g)?.label ?? 'None'

export interface IssueGroup {
  key: string
  label: string
  rows: GisIssue[]
}

const SEV_LABEL: Record<IssueSeverity, string> = { critical: 'Critical', non_critical: 'Non-critical' }
/** Canonical section order when grouping by severity/status (counts don't decide these). */
const SEV_ORDER = ['Critical', 'Non-critical']
const STATUS_ORDER = ['Open', 'Pending', 'Completed']

/** Same merged 3-state label as the list's Status chip (kept here so lib code never imports a component). */
export const issueStatusLabel = (i: GisIssue): string =>
  i.status === 'completed' ? 'Completed'
  : i.status === 'pending' ? 'Pending'
  : 'Open'

/** Per-dimension key extractors. Null-ish values fold into the same fallback buckets the old
 *  breakdown pivot used. */
const KEY_FOR: Record<Exclude<IssueGroupBy, 'none'>, (i: GisIssue, siteMandal: Map<string, string>) => string> = {
  site: (i) => i.siteName ?? 'Unassigned site',
  question: (i) => i.question ?? 'No linked question',
  mandal: (i, siteMandal) => (i.siteId !== null ? siteMandal.get(i.siteId) : undefined) ?? 'Unknown mandal',
  department: (i) => i.department ?? 'Unmapped',
  entity: (i) => i.entityName ?? '—',
  category: (i) => i.category ?? 'Uncategorised',
  severity: (i) => SEV_LABEL[i.severity],
  status: (i) => issueStatusLabel(i),
}

/**
 * Partition `rows` (already filtered + risk-sorted; order is preserved inside each group) into
 * sections. Severity/status use their canonical order; every other dimension sorts by count desc
 * so the heaviest bucket leads. Returns [] for `none` (caller renders the flat list).
 */
export function groupIssues(
  rows: GisIssue[],
  groupBy: IssueGroupBy,
  siteMandal: Map<string, string>,
): IssueGroup[] {
  if (groupBy === 'none') {
    return []
  }
  const keyFor = KEY_FOR[groupBy]
  const buckets = new Map<string, GisIssue[]>()
  for (const i of rows) {
    const key = keyFor(i, siteMandal)
    const list = buckets.get(key)
    if (list) {
      list.push(i)
    } else {
      buckets.set(key, [i])
    }
  }
  const groups = [...buckets.entries()].map(([key, list]) => ({
    key,
    label: groupBy === 'mandal' && key !== 'Unknown mandal' ? prettyName(key) : key,
    rows: list,
  }))
  const fixed = groupBy === 'severity' ? SEV_ORDER : groupBy === 'status' ? STATUS_ORDER : null
  if (fixed) {
    groups.sort((a, b) => fixed.indexOf(a.key) - fixed.indexOf(b.key))
  } else {
    groups.sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label))
  }
  return groups
}
